import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { IconLocationGroup } from '@assets/icons'
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react'
import { appColors } from '@theme/foundations/colors'
import { useTranslation } from 'react-i18next'
import { MdOutlineLocationOn } from 'react-icons/md'

interface LocationTagProps {
  locationId: number
  showGroupIcon?: boolean
  shrinkText?: boolean // OLD behavior
  truncate?: boolean // NEW behavior
  maxChars?: number
  /** When set (e.g. `"100%"`), tag fills the cell; label truncates with ellipsis + tooltip instead of shrinking to content width. */
  width?: string
}

export const LocationTag = ({
  locationId,
  showGroupIcon,
  shrinkText = false,
  truncate = false,
  maxChars = 20,
  width,
}: LocationTagProps) => {
  const { t } = useTranslation()
  const { locations, isAllLocation } = useLocationsContext()

  const getLocationName = (locationId: number) => {
    const location = locations?.find(l => l.locationPgId === locationId)
    if (location) return location.name

    const groupLocation = locations?.find(l => isAllLocation(l) && l.rawLocationPgId === locationId)
    if (groupLocation) return t('group')

    return undefined
  }

  const locationName = getLocationName(locationId)
  if (!locationName) return null

  const clamp = (str: string) => (str.length > maxChars ? str.slice(0, maxChars) + '…' : str)

  const icon = showGroupIcon ? <IconLocationGroup /> : <MdOutlineLocationOn size={13} color={appColors.secondary} />

  const fillCell = width != null && width !== ''
  const textValue = truncate && !fillCell ? clamp(locationName) : locationName

  const content = (
    <Box
      p='4px 8px'
      borderRadius='6px'
      bg={appColors.muted}
      h='26px'
      w={fillCell ? width : undefined}
      minW={fillCell ? 0 : undefined}
      maxW={fillCell ? undefined : shrinkText ? '110px' : truncate ? '180px' : 'fit-content'}
    >
      <Flex color={appColors.foreground} h='full' alignItems='center' gap='4px' minW={fillCell ? 0 : undefined}>
        <Box flexShrink={0}>{icon}</Box>
        <Text
          fontWeight={500}
          fontSize='12px'
          flex={fillCell ? 1 : undefined}
          minW={fillCell ? 0 : undefined}
          isTruncated={shrinkText || truncate}
          maxW={fillCell ? undefined : shrinkText ? '90px' : truncate ? '140px' : 'unset'}
          title={shrinkText ? locationName : undefined}
        >
          {textValue}
        </Text>
      </Flex>
    </Box>
  )

  return truncate ? (
    <Tooltip label={locationName} bg='white' color='gray.800' fontWeight='normal' p='2' borderRadius='base' hasArrow placement='top'>
      {content}
    </Tooltip>
  ) : (
    content
  )
}
